import { TeamMode } from './MenuUI';
import { p2pManager } from '../core/p2p';
import { BotDifficulty } from '../core/bots';

export interface MultiplayerCallbacks {
  onBack: () => void;
  onStartMatch: (isHost: boolean, mode: TeamMode) => void;
}

export class MultiplayerMenuUI {
  private container: HTMLDivElement | null = null;
  private callbacks!: MultiplayerCallbacks;
  private mode: TeamMode = { 
    team1Count: 1, 
    team2Count: 1, 
    keepTraces: true, 
    obstacleDensity: 'none',
    showCoordinates: false,
    turnTimer: 0,
    chatEquations: true
  };
  private playerName: string = 'Player';
  
  constructor() {}

  show(callbacks: MultiplayerCallbacks): void {
    this.destroy();
    this.callbacks = callbacks;

    const container = document.createElement('div');
    container.className = 'menu-container';
    container.id = 'multiplayer-menu';

    this.renderMainScreen(container);

    document.body.appendChild(container);
    this.container = container;
  }

  private renderMainScreen(container: HTMLDivElement): void {
    container.innerHTML = `
      <h1 class="menu-title">Multiplayer</h1>
      <p class="menu-subtitle" style="margin-bottom: 2rem;">CHOOSE A MODE</p>

      <div class="menu-buttons">
        <button class="menu-btn menu-btn--disabled menu-btn--secondary" disabled>
          Join Random Match
          <span class="menu-btn-badge">SOON</span>
        </button>
        <button class="menu-btn menu-btn--disabled menu-btn--secondary" disabled>
          Leaderboard
          <span class="menu-btn-badge">SOON</span>
        </button>
        <button class="menu-btn menu-btn--primary" id="btn-play-friends">
          Play with Friends
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-back" style="margin-top: 1rem;">
          Back to Menu
        </button>
      </div>
    `;

    container.querySelector('#btn-play-friends')?.addEventListener('click', () => {
      this.renderFriendsScreen(container);
    });
    container.querySelector('#btn-back')?.addEventListener('click', () => {
      this.callbacks.onBack();
    });
  }

  private renderFriendsScreen(container: HTMLDivElement): void {
    container.innerHTML = `
      <h1 class="menu-title" style="font-size: 2rem;">Play with Friends</h1>
      <p class="menu-subtitle" style="margin-bottom: 2rem;">HOST OR JOIN</p>

      <div class="menu-buttons">
        <button class="menu-btn menu-btn--primary" id="btn-host">
          Host a Game
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-join">
          Join a Game
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-back" style="margin-top: 1rem;">
          Back
        </button>
      </div>
    `;

    container.querySelector('#btn-host')?.addEventListener('click', () => {
      this.renderHostConfigScreen(container);
    });
    container.querySelector('#btn-join')?.addEventListener('click', () => {
      this.renderJoinScreen(container);
    });
    container.querySelector('#btn-back')?.addEventListener('click', () => {
      this.renderMainScreen(container);
    });
  }

  private renderHostConfigScreen(container: HTMLDivElement): void {
    container.innerHTML = `
      <h1 class="menu-title" style="font-size: 2rem;">Host Game</h1>
      
      <div class="menu-settings" style="width: 100%; max-width: 400px; margin-top: 1.5rem;">
        <div class="setting-group" style="margin-bottom: 1rem;">
          <label style="font-family: var(--font-ui); font-size: 14px; font-weight: 500;">Your Name</label>
          <input type="text" id="input-name" value="${localStorage.getItem('mathillery_player_name') || 'HostPlayer'}" style="width: 100%; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px; font-family: var(--font-ui);" />
        </div>

        <div class="setting-group" style="margin-bottom: 1rem;">
          <label style="font-family: var(--font-ui); font-size: 14px; font-weight: 500;">Match Settings</label>
          <div style="display: flex; gap: 10px; margin-top: 5px;">
            <select id="sel-soldiers" style="flex: 1; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px; font-family: var(--font-ui);">
              <option value="1" selected>1v1</option>
              <option value="2" disabled>2v2 (SOON)</option>
              <option value="3" disabled>3v3 (SOON)</option>
              <option value="4" disabled>4v4 (SOON)</option>
            </select>
            <select id="sel-obstacles" style="flex: 1; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px; font-family: var(--font-ui);">
              <option value="none" selected>No Obstacles</option>
              <option value="sparse">Sparse Obstacles</option>
              <option value="dense">Dense Obstacles</option>
            </select>
            <select id="sel-timer" style="flex: 1; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px; font-family: var(--font-ui);">
              <option value="0">No Timer</option>
              <option value="30">30s Turn</option>
              <option value="60" selected>60s Turn</option>
              <option value="90">90s Turn</option>
            </select>
          </div>
        </div>
        
        <div class="setting-group" style="display: flex; flex-direction: column; gap: 8px;">
          <label style="display: flex; align-items: center; gap: 8px; font-family: var(--font-ui); font-size: 14px; cursor: pointer;">
            <input type="checkbox" id="chk-traces" checked />
            Keep trajectory traces
          </label>
          <label style="display: flex; align-items: center; gap: 8px; font-family: var(--font-ui); font-size: 14px; cursor: pointer;">
            <input type="checkbox" id="chk-coords" />
            Show coordinate labels
          </label>
          <label style="display: flex; align-items: center; gap: 8px; font-family: var(--font-ui); font-size: 14px; cursor: pointer;">
            <input type="checkbox" id="chk-chateqs" checked />
            Show Equation Bubbles
          </label>
        </div>
      </div>

      <div class="menu-buttons" style="margin-top: 1.5rem;">
        <button class="menu-btn menu-btn--primary" id="btn-start-hosting">
          Create Game
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-back">
          Cancel
        </button>
      </div>
    `;

    container.querySelector('#btn-start-hosting')?.addEventListener('click', async () => {
      const name = (container.querySelector('#input-name') as HTMLInputElement).value || 'Host';
      localStorage.setItem('mathillery_player_name', name);
      const soldiers = parseInt((container.querySelector('#sel-soldiers') as HTMLSelectElement).value, 10);
      const obstacles = (container.querySelector('#sel-obstacles') as HTMLSelectElement).value as any;
      const turnTimer = parseInt((container.querySelector('#sel-timer') as HTMLSelectElement).value, 10);
      const selTimer = (container.querySelector('#sel-timer') as HTMLSelectElement).value;
      const chkTraces = (container.querySelector('#chk-traces') as HTMLInputElement).checked;
      const chkCoords = (container.querySelector('#chk-coords') as HTMLInputElement).checked;
      const chkChatEqs = (container.querySelector('#chk-chateqs') as HTMLInputElement).checked;
      
      this.mode = {
        team1Count: soldiers,
        team2Count: soldiers,
        obstacleDensity: obstacles,
        keepTraces: chkTraces,
        showCoordinates: chkCoords,
        turnTimer: parseInt(selTimer, 10),
        chatEquations: chkChatEqs
      };

      try {
        const btn = container.querySelector('#btn-start-hosting') as HTMLButtonElement;
        btn.textContent = 'Connecting...';
        btn.disabled = true;

        const code = await p2pManager.hostGame(name);
        this.renderWaitingRoom(container, code);
      } catch (err) {
        alert('Failed to connect to matchmaking server.');
        this.renderHostConfigScreen(container);
      }
    });

    container.querySelector('#btn-back')?.addEventListener('click', () => {
      this.renderFriendsScreen(container);
    });
  }

  private renderJoinScreen(container: HTMLDivElement): void {
    container.innerHTML = `
      <h1 class="menu-title" style="font-size: 2rem;">Join Game</h1>
      
      <div class="menu-settings" style="width: 100%; max-width: 320px; margin-top: 1.5rem;">
        <div class="setting-group" style="margin-bottom: 1rem;">
          <label style="font-family: var(--font-ui); font-size: 14px; font-weight: 500;">Your Name</label>
          <input type="text" id="input-name" value="${localStorage.getItem('mathillery_player_name') || 'GuestPlayer'}" style="width: 100%; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px; font-family: var(--font-ui);" />
        </div>
        <div class="setting-group" style="margin-bottom: 1rem;">
          <label style="font-family: var(--font-ui); font-size: 14px; font-weight: 500;">Room Code</label>
          <input type="text" id="input-code" placeholder="e.g. 1234" style="width: 100%; padding: 8px; border: 1px solid var(--color-border); border-radius: 8px; font-family: var(--font-ui); text-transform: uppercase;" />
        </div>
      </div>

      <div class="menu-buttons" style="margin-top: 1.5rem;">
        <button class="menu-btn menu-btn--primary" id="btn-join-room">
          Connect
        </button>
        <button class="menu-btn menu-btn--secondary" id="btn-back">
          Cancel
        </button>
      </div>
    `;

    container.querySelector('#btn-join-room')?.addEventListener('click', async () => {
      const name = (container.querySelector('#input-name') as HTMLInputElement).value || 'Guest';
      localStorage.setItem('mathillery_player_name', name);
      const code = (container.querySelector('#input-code') as HTMLInputElement).value.toUpperCase();
      
      if (!code) return alert('Enter a room code');

      try {
        const btn = container.querySelector('#btn-join-room') as HTMLButtonElement;
        btn.textContent = 'Connecting...';
        btn.disabled = true;

        await p2pManager.joinGame(code, name);
        // Once joined, wait for INIT_MATCH from host
        btn.textContent = 'Waiting for host to start...';
        
        p2pManager.on('START_GAME', (payload) => {
          this.callbacks.onStartMatch(false, payload.mode);
        });

      } catch (err) {
        alert('Failed to join room. Check the code.');
        this.renderJoinScreen(container);
      }
    });

    container.querySelector('#btn-back')?.addEventListener('click', () => {
      this.renderFriendsScreen(container);
    });
  }

  private renderWaitingRoom(container: HTMLDivElement, code: string): void {
    container.innerHTML = `
      <h1 class="menu-title" style="font-size: 2rem;">Waiting Room</h1>
      <p class="menu-subtitle" style="margin-bottom: 2rem; color: var(--color-team1);">ROOM CODE: <strong style="font-size: 16px;">${code}</strong></p>

      <div style="font-family: var(--font-ui); font-size: 16px; margin-bottom: 2rem; text-align: center;">
        Waiting for opponent to join...<br/>
        <span style="font-size: 12px; color: var(--color-text-muted);">Send them the room code.</span>
      </div>

      <div class="menu-buttons">
        <button class="menu-btn menu-btn--secondary" id="btn-cancel">
          Cancel
        </button>
      </div>
    `;

    p2pManager.on('HANDSHAKE', () => {
      // Send START_GAME and start scene
      p2pManager.send('START_GAME', { mode: this.mode });
      this.callbacks.onStartMatch(true, this.mode);
    });

    container.querySelector('#btn-cancel')?.addEventListener('click', () => {
      p2pManager.disconnect();
      this.renderHostConfigScreen(container);
    });
  }

  destroy(): void {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
      this.container = null;
    }
  }
}
